import _extends from "@babel/runtime/helpers/extends";
import React, { useCallback, useMemo } from 'react';
import ReactDOMServer from 'react-dom/server';
import { FormattedMessage, useIntl } from 'react-intl-next';
import Button from '@atlaskit/button';
import Form from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import Modal, { CloseButton, ModalBody, ModalFooter, ModalTransition, useModal } from '@atlaskit/modal-dialog';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Anchor, Box, Stack, Text, xcss } from '@atlaskit/primitives';
import { sendUIEvent } from '../../../../common/utils/analytics-client';
import { CONSENT_DISPLAYED_TEXT_ITEMS } from '../../../../services/consent-hub-service/constants';
import { PreferenceCategory } from '../../../../types';
import { CookieCategorySection } from './cookie-category-section';
import { i18n } from './messages';
var categoryNameStyles = xcss({
  fontWeight: 'font.weight.bold'
});
var customHeaderStyles = xcss({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexDirection: 'row-reverse'
});
var _extractTextFromReactNode = function extractTextFromReactNode(node) {
  if (typeof node === 'string') {
    return node;
  }
  if (typeof node === 'number') {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(_extractTextFromReactNode).join('');
  }
  if ( /*#__PURE__*/React.isValidElement(node)) {
    if (node.props.children) {
      return _extractTextFromReactNode(node.props.children);
    }
  }
  return '';
};
var getDefaultText = function getDefaultText(intl) {
  var link = intl.formatMessage(i18n.trackingLink);
  var defaultSummaryDescriptionDisplayedText = intl.formatMessage(i18n.summaryDescription, {
    link: link
  });
  var defaultStrictDescriptionDisplayedTextNode = intl.formatMessage(fg('product-terminology-refresh') ? i18n.strictDescriptionAppify : i18n.strictDescription, {
    bold: function bold(msg) {
      return msg;
    }
  });

  // ReactIntl requires interpolated values to be passed in, and this particular string requires a <bold> modifier.
  // Unfortunately, this converts the above formatted message to a ReactNode but ConsentHub needs strings for `displayedText`,
  // thus, we have to render to a static HTML string first and sanitize the result
  var defaultStrictDescriptionDisplayedText = fg('cpals_refactor_text_rendering_in_preferences_modal') ? _extractTextFromReactNode(defaultStrictDescriptionDisplayedTextNode) : ReactDOMServer.renderToStaticMarkup(defaultStrictDescriptionDisplayedTextNode);
  var defaultDisplayedText = "".concat(defaultSummaryDescriptionDisplayedText, ". ").concat(defaultStrictDescriptionDisplayedText);
  var sanitizedText = defaultDisplayedText.replace(/&#x27;/g, "'");
  return sanitizedText;
};
var getCookieCategories = function getCookieCategories(intl) {
  var cookieCategoriesToTranslate = [{
    // Tracks Functional related cookies like preferences
    category: PreferenceCategory.Functional,
    title: i18n.functionalOption,
    description: i18n.functionalOptionDescription
  }, {
    // Tracks Performance related cookies like analytics
    category: PreferenceCategory.Analytics,
    title: i18n.analyticalOption,
    description: fg('product-terminology-refresh') ? i18n.analyticalOptionDescriptionAppify : i18n.analyticalOptionDescription
  }, {
    // Tracks Targeting related cookies like marketing and advertising
    category: PreferenceCategory.Marketing,
    title: i18n.marketingOption,
    description: i18n.marketingOptionDescription
  }];

  // We'll use this mapping to send the `displayedText` for each checkbox when saving to ConsentHub
  var displayedTextMap = CONSENT_DISPLAYED_TEXT_ITEMS.reduce(function (translatedTextMap, category) {
    // Set the description as the default for all items (including items with checkboxes in the UI)
    // and we'll override as needed. Prefs that are not visually displayed in the UI anymore will use this default.
    translatedTextMap[category] = getDefaultText(intl);
    return translatedTextMap;
  }, {});
  var checkboxes = cookieCategoriesToTranslate.map(function (_ref) {
    var category = _ref.category,
      title = _ref.title,
      description = _ref.description;
    var translatedTitle = intl.formatMessage(title);
    var translatedDescription = intl.formatMessage(description);

    // If we are showing individual checkboxes, use that description for auditing purposes
    displayedTextMap[category] = translatedDescription;
    return {
      category: category,
      titleElement: /*#__PURE__*/React.createElement(Box, {
        xcss: categoryNameStyles
      }, translatedTitle),
      descriptionElement: /*#__PURE__*/React.createElement(Box, null, translatedDescription),
      titleString: title.defaultMessage
    };
  });
  return {
    checkboxes: checkboxes,
    displayedTextMap: displayedTextMap
  };
};
var noop = function noop() {};
var CustomHeader = function CustomHeader(_ref2) {
  var onClose = _ref2.onClose;
  var _useModal = useModal(),
    titleId = _useModal.titleId;
  return /*#__PURE__*/React.createElement(Box, {
    xcss: customHeaderStyles,
    padding: "space.300"
  }, /*#__PURE__*/React.createElement(CloseButton, {
    onClick: onClose,
    testId: "close-modal-button"
  }), /*#__PURE__*/React.createElement(Heading, {
    as: "h1",
    size: "medium",
    id: titleId
  }, /*#__PURE__*/React.createElement(FormattedMessage, i18n.title)));
};
export var PresentationalPreferenceModal = function PresentationalPreferenceModal(_ref3) {
  var preferences = _ref3.preferences,
    _ref3$setPreferencesD = _ref3.setPreferencesData,
    setPreferencesData = _ref3$setPreferencesD === void 0 ? noop : _ref3$setPreferencesD,
    _ref3$onSubmit = _ref3.onSubmit,
    onSubmit = _ref3$onSubmit === void 0 ? noop : _ref3$onSubmit,
    _ref3$onClose = _ref3.onClose,
    onClose = _ref3$onClose === void 0 ? noop : _ref3$onClose,
    _ref3$setIsOpen = _ref3.setIsOpen,
    setIsOpen = _ref3$setIsOpen === void 0 ? noop : _ref3$setIsOpen;
  var intl = useIntl();
  var onCloseModal = useCallback(function () {
    setIsOpen(false);
    onClose();
  }, [setIsOpen, onClose]);
  var onClickCancel = useCallback(function () {
    onCloseModal();
    sendUIEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'cancel',
      source: 'cookieConsentModal'
    });
  }, [onCloseModal]);
  var onClickClose = useCallback(function () {
    onCloseModal();
    sendUIEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'close',
      source: 'cookieConsentModal'
    });
  }, [onCloseModal]);
  var onClickBackground = useCallback(function () {
    onCloseModal();
    sendUIEvent({
      action: 'clicked',
      actionSubject: 'area',
      actionSubjectId: 'background',
      source: 'cookieConsentModal'
    });
  }, [onCloseModal]);
  var onClickTrackingNoticeLink = useCallback(function () {
    sendUIEvent({
      action: 'clicked',
      actionSubject: 'link',
      actionSubjectId: 'cookiesTrackingNotice',
      source: 'cookieConsentModal'
    });
  }, []);
  var cookieCategories = useMemo(function () {
    return getCookieCategories(intl);
  }, [intl]);
  var handleSubmission = useCallback(function () {
    var displayedTextMap = cookieCategories.displayedTextMap;
    onSubmit(displayedTextMap);
  }, [onSubmit, cookieCategories]);
  return /*#__PURE__*/React.createElement(ModalTransition, null, /*#__PURE__*/React.createElement(Modal, {
    onClose: onClickBackground
  }, fg('platform-design_system_team-form_conversion') ? /*#__PURE__*/React.createElement(Form, {
    onSubmit: handleSubmission
  }, /*#__PURE__*/React.createElement(CustomHeader, {
    onClose: onClickClose
  }), /*#__PURE__*/React.createElement(ModalBody, null, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, i18n.summaryDescription, {
    values: {
      link: /*#__PURE__*/React.createElement(Anchor, {
        href: "https://www.atlassian.com/legal/cookies#cookies-and-other-tracking-technologies",
        target: "_blank",
        onClick: onClickTrackingNoticeLink
      }, /*#__PURE__*/React.createElement(FormattedMessage, i18n.trackingLink))
    },
    tagName: 'p'
  })), /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, fg('product-terminology-refresh') ? i18n.strictDescriptionAppify : i18n.strictDescription, {
    values: {
      bold: function bold(msg) {
        return /*#__PURE__*/React.createElement(Text, {
          weight: "bold"
        }, msg);
      }
    },
    tagName: 'p'
  }))), cookieCategories.checkboxes.map(function (_ref4, index) {
    var _ref5;
    var category = _ref4.category,
      titleElement = _ref4.titleElement,
      titleString = _ref4.titleString,
      description = _ref4.descriptionElement;
    return /*#__PURE__*/React.createElement(Stack, {
      key: index
    }, /*#__PURE__*/React.createElement(CookieCategorySection, {
      category: category,
      titleElement: titleElement,
      titleString: titleString,
      descriptionElement: description,
      isChecked: (_ref5 = preferences && preferences[category]) !== null && _ref5 !== void 0 ? _ref5 : false,
      setPreferencesData: setPreferencesData
    }));
  })), /*#__PURE__*/React.createElement(ModalFooter, null, /*#__PURE__*/React.createElement(Button, {
    appearance: "subtle",
    onClick: onClickCancel
  }, /*#__PURE__*/React.createElement(FormattedMessage, i18n.cancelBtn)), /*#__PURE__*/React.createElement(Button, {
    type: "submit",
    appearance: "primary",
    onClick: handleSubmission
  }, /*#__PURE__*/React.createElement(FormattedMessage, i18n.confirmBtn)))) : /*#__PURE__*/React.createElement(Form, {
    onSubmit: handleSubmission
  }, function (_ref6) {
    var formProps = _ref6.formProps;
    return /*#__PURE__*/React.createElement("form", formProps, /*#__PURE__*/React.createElement(CustomHeader, {
      onClose: onClickClose
    }), /*#__PURE__*/React.createElement(ModalBody, null, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, i18n.summaryDescription, {
      values: {
        link: /*#__PURE__*/React.createElement(Anchor, {
          href: "https://www.atlassian.com/legal/cookies#cookies-and-other-tracking-technologies",
          target: "_blank",
          onClick: onClickTrackingNoticeLink
        }, /*#__PURE__*/React.createElement(FormattedMessage, i18n.trackingLink))
      },
      tagName: 'p'
    })), /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, fg('product-terminology-refresh') ? i18n.strictDescriptionAppify : i18n.strictDescription, {
      values: {
        bold: function bold(msg) {
          return /*#__PURE__*/React.createElement(Text, {
            weight: "bold"
          }, msg);
        }
      },
      tagName: 'p'
    }))), cookieCategories.checkboxes.map(function (_ref7, index) {
      var _ref8;
      var category = _ref7.category,
        titleElement = _ref7.titleElement,
        titleString = _ref7.titleString,
        description = _ref7.descriptionElement;
      return /*#__PURE__*/React.createElement(Stack, {
        key: index
      }, /*#__PURE__*/React.createElement(CookieCategorySection, {
        category: category,
        titleElement: titleElement,
        titleString: titleString,
        descriptionElement: description,
        isChecked: (_ref8 = preferences && preferences[category]) !== null && _ref8 !== void 0 ? _ref8 : false,
        setPreferencesData: setPreferencesData
      }));
    })), /*#__PURE__*/React.createElement(ModalFooter, null, /*#__PURE__*/React.createElement(Button, {
      appearance: "subtle",
      onClick: onClickCancel
    }, /*#__PURE__*/React.createElement(FormattedMessage, i18n.cancelBtn)), /*#__PURE__*/React.createElement(Button, {
      type: "submit",
      appearance: "primary",
      onClick: handleSubmission
    }, /*#__PURE__*/React.createElement(FormattedMessage, i18n.confirmBtn))));
  })));
};